import React, { useEffect, useState } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Alert, Stack, MenuItem, FormControl, InputLabel, Select } from '@mui/material';
import { DataGrid, GridColDef, GridActionsColDef, GridActionsCellItem, GridToolbar } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PageHeader from '../components/PageHeader';
import CustomNoRowsOverlay from '../components/CustomNoRowsOverlay';
import api from '../api/api';
import { useAuth } from '../context/AuthContext';
import DialogContentText from '@mui/material/DialogContentText';
import { useFeedback } from '../context/FeedbackContext';

interface Course {
  id: number;
  course_code: string;
  title: string;
  school_id: number;
  school?: { id: number; name: string };
  class_level?: string;
  credits?: number;
  description?: string;
  prerequisites?: string;
  max_students?: number;
  status: string;
}

interface School {
  id: number;
  name: string;
}

const classLevels = [
  'Pre-Primary 1', 'Pre-Primary 2', 'Grade 1', 'Grade 2', 'Grade 3', 'Grade 4', 'Grade 5',
  'Grade 6', 'Grade 7', 'Grade 8', 'Grade 9', 'Grade 10', 'Grade 11', 'Grade 12'
];

const courseStatuses = ['active', 'inactive', 'archived'];

const Courses: React.FC = () => {
  const { user } = useAuth();
  const [courses, setCourses] = useState<Course[]>([]);
  const [schools, setSchools] = useState<School[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState<Course | null>(null);
  const [courseCode, setCourseCode] = useState('');
  const [courseTitle, setCourseTitle] = useState('');
  const [courseSchoolId, setCourseSchoolId] = useState<number | ''>('');
  const [classLevel, setClassLevel] = useState('');
  const [credits, setCredits] = useState<number | ''>('');
  const [description, setDescription] = useState('');
  const [prerequisites, setPrerequisites] = useState('');
  const [maxStudents, setMaxStudents] = useState<number | ''>('');
  const [status, setStatus] = useState('active');
  const [actionError, setActionError] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [courseToDelete, setCourseToDelete] = useState<Course | null>(null);
  const { showSuccess, showError } = useFeedback();

  const fetchCourses = async () => {
    setLoading(true);
    setError('');
    try {
      let schoolId = user?.school_id;
      if (user?.role === 'superuser') {
        const schoolsRes = await api.get('/schools/');
        setSchools(schoolsRes.data as School[]);
        // For superuser, fetch all courses for all schools
        const allCourses: Course[] = [];
        for (const school of schoolsRes.data as School[]) {
          const res = await api.get('/courses/', { params: { school_id: school.id } });
          allCourses.push(...(res.data as Course[]));
        }
        setCourses(allCourses);
      } else {
        const res = await api.get('/courses/', { params: { school_id: schoolId } });
        setCourses(res.data as Course[]);
      }
    } catch (err: any) {
      setError('Failed to load courses');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      fetchCourses();
    }
  }, [user]);

  const resetForm = () => {
    setCourseCode('');
    setCourseTitle('');
    setCourseSchoolId('');
    setClassLevel('');
    setCredits('');
    setDescription('');
    setPrerequisites('');
    setMaxStudents('');
    setStatus('active');
  };

  const handleAdd = async () => {
    setActionError('');
    try {
      await api.post('/courses', {
        course_code: courseCode,
        title: courseTitle,
        school_id: user?.role === 'superuser' ? courseSchoolId : user?.school_id,
        class_level: classLevel || undefined,
        credits: credits || undefined,
        description: description || undefined,
        prerequisites: prerequisites || undefined,
        max_students: maxStudents || undefined,
        status: status,
      });
      setAddDialogOpen(false);
      resetForm();
      fetchCourses();
      showSuccess('Course added successfully!');
    } catch (err: any) {
      setActionError(err?.response?.data?.detail || 'Failed to add course');
      showError(err?.response?.data?.detail || 'Failed to add course');
    }
  };

  const handleEdit = async () => {
    if (!selectedCourse) return;
    setActionError('');
    try {
      await api.put(`/courses/${selectedCourse.id}`, {
        course_code: courseCode,
        title: courseTitle,
        school_id: user?.role === 'superuser' ? courseSchoolId : user?.school_id,
        class_level: classLevel || undefined,
        credits: credits || undefined,
        description: description || undefined,
        prerequisites: prerequisites || undefined,
        max_students: maxStudents || undefined,
        status: status,
      });
      setEditDialogOpen(false);
      setSelectedCourse(null);
      resetForm();
      fetchCourses();
      showSuccess('Course updated successfully!');
    } catch (err: any) {
      setActionError(err?.response?.data?.detail || 'Failed to update course');
      showError(err?.response?.data?.detail || 'Failed to update course');
    }
  };

  const handleDelete = async (id: number) => {
    setActionError('');
    try {
      await api.delete(`/courses/${id}`);
      fetchCourses();
      showSuccess('Course deleted successfully!');
    } catch (err: any) {
      setActionError('Failed to delete course');
      showError('Failed to delete course');
    }
  };

  const handleDeleteClick = (course: Course) => {
    setCourseToDelete(course);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (courseToDelete) {
      await handleDelete(courseToDelete.id);
      setCourseToDelete(null);
      setDeleteDialogOpen(false);
    }
  };

  const handleCancelDelete = () => {
    setCourseToDelete(null);
    setDeleteDialogOpen(false);
  };

  const actionsColumn: GridActionsColDef = {
    field: 'actions',
    type: 'actions',
    headerName: 'Actions',
    width: 150,
    getActions: (params: any) => [
      <GridActionsCellItem
        icon={<EditIcon />}
        label="Edit"
        onClick={() => {
          setSelectedCourse(params.row);
          setCourseCode(params.row.course_code);
          setCourseTitle(params.row.title);
          setCourseSchoolId(params.row.school_id);
          setClassLevel(params.row.class_level || '');
          setCredits(params.row.credits || '');
          setDescription(params.row.description || '');
          setPrerequisites(params.row.prerequisites || '');
          setMaxStudents(params.row.max_students || '');
          setStatus(params.row.status || 'active');
          setEditDialogOpen(true);
        }}
      />,
      <GridActionsCellItem
        icon={<DeleteIcon />}
        label="Delete"
        onClick={() => handleDeleteClick(params.row)}
      />,
    ],
  };

  const columns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 90 },
    { field: 'course_code', headerName: 'Course Code', flex: 1, minWidth: 120 },
    { field: 'title', headerName: 'Title', flex: 1, minWidth: 180 },
    { field: 'class_level', headerName: 'Class Level', width: 120 },
    { field: 'max_students', headerName: 'Max Students', width: 120 },
    { field: 'status', headerName: 'Status', width: 100 },
    ...(user?.role === 'superuser'
      ? [{ field: 'school', headerName: 'School', flex: 1, minWidth: 150, valueGetter: (params: any) => params.row.school?.name }]
      : []),
    actionsColumn,
  ].filter(Boolean);

  if (!user) return null;
  if (user.role !== 'admin' && user.role !== 'superuser') {
    return <Alert severity="error">You are not authorized to view this page.</Alert>;
  }

  return (
    <Box>
      <PageHeader title="Course Management">
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setAddDialogOpen(true)}>
          Add Course
        </Button>
      </PageHeader>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Box sx={{ height: 500, width: '100%' }}>
        <DataGrid
          rows={courses}
          columns={columns}
          loading={loading}
          pageSizeOptions={[10, 25, 50]}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          slots={{ toolbar: GridToolbar, noRowsOverlay: () => <CustomNoRowsOverlay message="No Courses Found" /> }}
          disableRowSelectionOnClick
          getRowId={row => row.id}
        />
      </Box>
      {/* Add Dialog */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Add Course</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Course Code"
              value={courseCode}
              onChange={e => setCourseCode(e.target.value)}
              fullWidth
              required
            />
            <TextField
              label="Title"
              value={courseTitle}
              onChange={e => setCourseTitle(e.target.value)}
              fullWidth
              required
            />
            {user.role === 'superuser' && (
              <TextField
                select
                label="School"
                value={courseSchoolId}
                onChange={e => setCourseSchoolId(Number(e.target.value))}
                fullWidth
                required
              >
                {schools.map(school => (
                  <MenuItem key={school.id} value={school.id}>{school.name}</MenuItem>
                ))}
              </TextField>
            )}
            <FormControl fullWidth>
              <InputLabel>Class Level</InputLabel>
              <Select
                value={classLevel}
                onChange={e => setClassLevel(e.target.value)}
                label="Class Level"
              >
                <MenuItem value="">None</MenuItem>
                {classLevels.map(level => (
                  <MenuItem key={level} value={level}>{level}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Credits (Optional - not used in CBC)"
              type="number"
              value={credits}
              onChange={e => setCredits(e.target.value ? Number(e.target.value) : '')}
              fullWidth
              helperText="Credits are not used in the Kenyan CBC curriculum but can be used for other purposes"
            />
            <TextField
              label="Description"
              value={description}
              onChange={e => setDescription(e.target.value)}
              fullWidth
              multiline
              rows={3}
            />
            <TextField
              label="Prerequisites"
              value={prerequisites}
              onChange={e => setPrerequisites(e.target.value)}
              fullWidth
              multiline
              rows={2}
            />
            <TextField
              label="Max Students"
              type="number"
              value={maxStudents}
              onChange={e => setMaxStudents(e.target.value ? Number(e.target.value) : '')}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={status}
                onChange={e => setStatus(e.target.value)}
                label="Status"
              >
                {courseStatuses.map(status => (
                  <MenuItem key={status} value={status}>{status}</MenuItem>
                ))}
              </Select>
            </FormControl>
            {actionError && <Alert severity="error">{actionError}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleAdd} variant="contained">Add</Button>
        </DialogActions>
      </Dialog>
      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit Course</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Course Code"
              value={courseCode}
              onChange={e => setCourseCode(e.target.value)}
              fullWidth
              required
            />
            <TextField
              label="Title"
              value={courseTitle}
              onChange={e => setCourseTitle(e.target.value)}
              fullWidth
              required
            />
            {user.role === 'superuser' && (
              <TextField
                select
                label="School"
                value={courseSchoolId}
                onChange={e => setCourseSchoolId(Number(e.target.value))}
                fullWidth
                required
              >
                {schools.map(school => (
                  <MenuItem key={school.id} value={school.id}>{school.name}</MenuItem>
                ))}
              </TextField>
            )}
            <FormControl fullWidth>
              <InputLabel>Class Level</InputLabel>
              <Select
                value={classLevel}
                onChange={e => setClassLevel(e.target.value)}
                label="Class Level"
              >
                <MenuItem value="">None</MenuItem>
                {classLevels.map(level => (
                  <MenuItem key={level} value={level}>{level}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Credits (Optional - not used in CBC)"
              type="number"
              value={credits}
              onChange={e => setCredits(e.target.value ? Number(e.target.value) : '')}
              fullWidth
              helperText="Credits are not used in the Kenyan CBC curriculum but can be used for other purposes"
            />
            <TextField
              label="Description"
              value={description}
              onChange={e => setDescription(e.target.value)}
              fullWidth
              multiline
              rows={3}
            />
            <TextField
              label="Prerequisites"
              value={prerequisites}
              onChange={e => setPrerequisites(e.target.value)}
              fullWidth
              multiline
              rows={2}
            />
            <TextField
              label="Max Students"
              type="number"
              value={maxStudents}
              onChange={e => setMaxStudents(e.target.value ? Number(e.target.value) : '')}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={status}
                onChange={e => setStatus(e.target.value)}
                label="Status"
              >
                {courseStatuses.map(status => (
                  <MenuItem key={status} value={status}>{status}</MenuItem>
                ))}
              </Select>
            </FormControl>
            {actionError && <Alert severity="error">{actionError}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleEdit} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>
      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleCancelDelete}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the course "{courseToDelete?.title}"?
          </DialogContentText>
          {actionError && <Alert severity="error">{actionError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete}>Cancel</Button>
          <Button onClick={handleConfirmDelete} variant="contained" color="error">Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Courses; 